import lightGallery from "lightgallery";
import type { LightGallery as ILightGallery } from "lightgallery/lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import { MessageCircle, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-zoom.css";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface PromoImageView {
  id: string;
  url: string;
  sortOrder: number;
}

interface PromoEventView {
  id: string;
  whatsappNumber: string;
  images: PromoImageView[];
}

interface WhatsAppPromoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promo: PromoEventView;
}

/** Build a wa.me URL from a phone string (digits incl. country code, no "+"). */
function buildWhatsAppUrl(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 6) return null;
  return `https://wa.me/${digits}`;
}

/** Display the number with a leading "+" (keeps any spacing the admin entered). */
function formatPhoneDisplay(phone: string): string {
  const trimmed = phone.trim();
  return trimmed.startsWith("+") ? trimmed : `+${trimmed.replace(/[^\d]/g, "")}`;
}

export function WhatsAppPromoDialog({ open, onOpenChange, promo }: WhatsAppPromoDialogProps) {
  const { t } = useTranslation("common");
  const containerRef = useRef<HTMLDivElement>(null);
  const lgRef = useRef<ILightGallery | null>(null);

  const [api, setApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState(0);

  const images = promo.images;
  const whatsAppUrl = buildWhatsAppUrl(promo.whatsappNumber);
  const isSingle = images.length === 1;

  const dynamicEl = useMemo(
    () => images.map((image) => ({ src: image.url, thumb: image.url })),
    [images]
  );

  useEffect(() => {
    if (!containerRef.current || dynamicEl.length === 0) return;

    const lg = lightGallery(containerRef.current, {
      dynamic: true,
      dynamicEl,
      plugins: [lgZoom, lgThumbnail],
      licenseKey: "0000-0000-000-0000",
      speed: 500,
      download: false,
    });

    lgRef.current = lg;

    return () => {
      lg.destroy();
      lgRef.current = null;
    };
  }, [dynamicEl]);

  // Keep the active-slide indicator (dots + counter) in sync with the carousel.
  useEffect(() => {
    if (!api) return;
    setSelected(api.selectedScrollSnap());
    const onSelect = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  const openZoom = (index: number) => {
    lgRef.current?.openGallery(index);
  };

  const imageClass = "mx-auto max-h-[70vh] w-full object-contain";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Chrome-free, image-first: no padding/border, image goes edge-to-edge
          and all controls float over it. */}
      <DialogContent
        hideClose
        className="block w-[calc(100vw-2rem)] max-w-md gap-0 overflow-hidden rounded-2xl border-0 p-0 shadow-xl"
      >
        <div className="relative bg-black">
          {isSingle ? (
            <button
              type="button"
              onClick={() => openZoom(0)}
              className="block w-full cursor-zoom-in"
            >
              <img src={images[0]?.url} alt="" className={imageClass} />
            </button>
          ) : (
            <Carousel setApi={setApi} opts={{ loop: true, align: "center" }} className="w-full">
              <CarouselContent>
                {images.map((image, index) => (
                  <CarouselItem key={image.id}>
                    <button
                      type="button"
                      onClick={() => openZoom(index)}
                      className="block w-full cursor-zoom-in"
                    >
                      <img src={image.url} alt="" className={imageClass} />
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Arrows overlay the image edges. */}
              <CarouselPrevious className="left-2 border-0 bg-black/40 text-white hover:bg-black/60 hover:text-white" />
              <CarouselNext className="right-2 border-0 bg-black/40 text-white hover:bg-black/60 hover:text-white" />

              {/* Counter, top-left. */}
              <div className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/50 px-2 py-0.5 text-xs font-medium text-white">
                {selected + 1}/{images.length}
              </div>

              {/* Dots overlay bottom-center, with a scrim for legibility. */}
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1.5 bg-gradient-to-t from-black/50 to-transparent px-4 pb-3 pt-8">
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    aria-label={`Go to image ${index + 1}`}
                    onClick={() => api?.scrollTo(index)}
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      index === selected ? "w-4 bg-white" : "w-1.5 bg-white/50"
                    )}
                  />
                ))}
              </div>
            </Carousel>
          )}

          {/* Close, top-right — over the image so it needs its own backdrop. */}
          <button
            type="button"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
            className="absolute right-2 top-2 z-10 rounded-full bg-black/50 p-1.5 text-white transition-colors hover:bg-black/70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {whatsAppUrl && (
          <a
            href={whatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 bg-[#25D366] px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-[#1ebe57]"
          >
            <MessageCircle className="h-4 w-4 shrink-0" />
            <span>{t("whatsappPromo.cta", "Contact")}</span>
            <span className="font-semibold tabular-nums">
              {formatPhoneDisplay(promo.whatsappNumber)}
            </span>
          </a>
        )}

        {/* Hidden container that hosts the lightGallery zoom lightbox. */}
        <div ref={containerRef} className="hidden" aria-hidden="true" />
      </DialogContent>
    </Dialog>
  );
}
